/**
 * Phone validation for signup (and similar forms).
 * Uses libphonenumber-js metadata so we do not hardcode country length tables.
 * Blank and dial-code-only values (auto-prefixed from the region select) are
 * treated as empty. Empty is valid unless `required` is true. Listed ISO
 * regions validate national length/pattern for that country. "OTHER" has no
 * dial code and uses a generic digit-length check.
 */
import { parsePhoneNumberFromString } from "libphonenumber-js";

/** Region code for "Others (none of the above)" — not an ISO country. */
export const OTHER_REGION_CODE = "OTHER";

const OTHER_PHONE_MIN_DIGITS = 7;
const OTHER_PHONE_MAX_DIGITS = 15;

/**
 * Returns a user-facing error for a phone field, or "" when valid.
 * @param {string} phone - Raw phone input (may include spaces / dial code)
 * @param {string} regionCode - ISO 3166-1 alpha-2 (e.g. "IN") or OTHER_REGION_CODE
 * @param {string} [dialCode] - Expected dial code for the region (e.g. "+91");
 *   when the input is only this dial code, treat as empty. Empty for OTHER.
 * @param {{ required?: boolean }} [options] - When required, empty / dial-code-only is an error.
 */
export function getPhoneError(phone, regionCode, dialCode = "", { required = false } = {}) {
  const trimmed = String(phone || "").replace(/\s/g, "");
  const dialNormalized = String(dialCode || "").replace(/\s/g, "");
  const isEmpty = !trimmed || (dialNormalized && trimmed === dialNormalized);
  if (isEmpty) {
    return required ? "Phone number is required" : "";
  }
  if (!regionCode) {
    return "Select your region first.";
  }

  const digits = trimmed.replace(/\D/g, "");
  if (String(regionCode).toUpperCase() === OTHER_REGION_CODE) {
    if (digits.length < OTHER_PHONE_MIN_DIGITS || digits.length > OTHER_PHONE_MAX_DIGITS) {
      return "Enter a valid phone number.";
    }
    return "";
  }

  if (dialNormalized && !trimmed.startsWith(dialNormalized)) {
    return "Enter a valid phone number for the selected region.";
  }
  const parsed = parsePhoneNumberFromString(trimmed, regionCode);
  if (!parsed || !parsed.isValid()) {
    return "Enter a valid phone number for the selected region.";
  }
  return "";
}
